import {Loader} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useEffect, useState} from 'react';
import {COMMENT_LIST_LIMIT} from '../../../../core/entities/community/constants';
import {
  type CommentListStatus,
  CommentListStatusType,
  type CommunityTopic,
} from '../../../../core/entities/community/schema';
import {useGetCommunityCommentList} from '../../../../core/repositories/communityRepository';
import {CommunitySearchForm} from './CommunitySearchForm';
import {PostCommentForm} from './PostCommentForm';
import {PostCommentFormAuthBox} from './PostCommentFormAuthBox';
import {CommentListItem} from './Presenter/CommentListItem';
import {CommentListPaging} from './Presenter/CommentListPaging';
import {PostButton} from './Presenter/PostButton';
import {UnauthenticatedCommentList} from './Presenter/UnauthenticatedCommentList';
import {ReplyModal} from './ReplyModal';
import {TopicDetail} from './TopicDetail';

type Props = {
  topic: CommunityTopic;
  communityNickname: string;
  isSignedIn: boolean;
  isStudentVerificationRequired: boolean;
};

export const CommunityDetailPcComponent: React.FC<Props> = ({
  topic,
  communityNickname,
  isSignedIn,
  isStudentVerificationRequired,
}: Props) => {
  const [commentListStatus, setCommentListStatus] = useState<CommentListStatus>(
    CommentListStatusType.latest,
  );
  const [searchWords, setSearchWords] = useState<string>(''); // 検索ワード
  const [pageCursor, setPageCursor] = useState<number>(0); // ページング
  const [isBackward, setIsBackward] = useState<boolean>(true); // 昇順にページングする
  const [isInitial, setIsInitial] = useState<boolean>(true); // 最新50件表示かどうか

  const [repliedSeqNumber, setRepliedSeqNumber] = useState<number>(0); // 返信元を表示するときのseqNumber管理用
  const [isReplyModalOpened, {open: openReplyModal, close: closeReplyModal}] =
    useDisclosure(false); // 返信元を表示するときのモーダル管理用
  const [seqNumberToReply, setSeqNumberToReply] = useState<number>(0); // 返信するボタンを押したときのseqNumber管理用

  const postCommentFormRef = React.useRef<HTMLDivElement>(null);
  const topItemRef = React.useRef<HTMLDivElement>(null);
  const bottomItemRef = React.useRef<HTMLDivElement>(null);

  const openReplyModalFunc = (seqNumber: number) => {
    setRepliedSeqNumber(seqNumber);
    openReplyModal();
  };

  const scrollToCommentForm = () => {
    if (postCommentFormRef.current) {
      postCommentFormRef.current.scrollIntoView({behavior: 'smooth'});
    }
  };

  const scrollToTopItem = () => {
    if (topItemRef.current) {
      topItemRef.current.scrollIntoView({behavior: 'smooth'});
    }
  };

  const scrollToBottomItem = () => {
    if (bottomItemRef.current) {
      bottomItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const onClickReplyButton = (seqNumber: number) => {
    setSeqNumberToReply(seqNumber);
    scrollToCommentForm();
  };

  const {data, isFetching} = useGetCommunityCommentList({
    topicId: topic.id,
    limit: COMMENT_LIST_LIMIT,
    keyword: searchWords ?? undefined,
    cursor: pageCursor === 0 ? undefined : pageCursor,
    backward: isBackward,
    initial: isInitial,
    config: {
      enabled: isSignedIn,
    },
  });

  useEffect(() => {
    if (pageCursor === 1) {
      setCommentListStatus(CommentListStatusType.oldest);
      return;
    }

    if (isInitial) {
      setCommentListStatus(CommentListStatusType.latest);
      return;
    }

    setCommentListStatus(CommentListStatusType.other);
  }, [pageCursor, isInitial]);

  return (
    <>
      <div className="bg-white rounded-12 shadow-modal">
        <div className="p-16 border-b-[1px] border-line border-collapse">
          <div className={'flex justify-between items-center'}>
            <div className="flex items-center w-[calc(100%-124px)] mr-8">
              <h1 className="text-[24px] text-black-1 font-bold leading">
                {topic.title}
              </h1>
            </div>
            <PostButton
              onClick={() => {
                scrollToCommentForm();
              }}
            />
          </div>
        </div>

        <div className="p-16 border-b-[1px] border-line border-collapse">
          <CommunitySearchForm
            placeholder="コメントを検索"
            setSearchWords={setSearchWords}
          />
        </div>

        <div className="p-16 border-b-[1px] border-line border-collapse">
          <TopicDetail topic={topic} isSignedIn={isSignedIn} />
        </div>
        <div ref={topItemRef}>
          <div ref={topItemRef} />
          {!isSignedIn ? (
            <UnauthenticatedCommentList
              isStudentVerificationRequired={isStudentVerificationRequired}
            />
          ) : isFetching ? (
            <div className="flex justify-center items-center h-[100px]">
              <Loader color="gray" />
            </div>
          ) : data?.ok && data.value.comments.length > 0 ? (
            <ul className="p-16">
              {data.value.comments.map((comment) => (
                <CommentListItem
                  key={`topic-${topic.id}-comment-${comment.id}`}
                  topicId={topic.id}
                  comment={comment}
                  openReplyModalFunc={openReplyModalFunc}
                  onClickReplyButton={onClickReplyButton}
                  isSignedIn={isSignedIn}
                />
              ))}
            </ul>
          ) : (
            <p className="py-36 px-16 text-base text-black-1 leading text-center">
              コメントはまだありません
            </p>
          )}
          <div ref={bottomItemRef} />
        </div>
        <CommentListPaging
          hasItem={(data?.ok && data.value.comments.length > 0) ?? false}
          hasPaging={
            (data?.ok && data.value.hitCount >= COMMENT_LIST_LIMIT) ?? false
          }
          previousCursor={
            data?.ok && data?.value.previousCursor
              ? data?.value.previousCursor
              : 0
          }
          nextCursor={
            data?.ok && data?.value.nextCursor ? data?.value.nextCursor : 0
          }
          setPageCursor={setPageCursor}
          setIsBackward={setIsBackward}
          setIsInitial={setIsInitial}
          commentListStatus={commentListStatus}
          scrollToTopItem={scrollToTopItem}
          scrollToBottomItem={scrollToBottomItem}
        />
      </div>
      <div ref={postCommentFormRef}>
        {isSignedIn ? (
          <PostCommentForm
            communityNickname={communityNickname}
            topicId={topic.id}
            seqNumberToReply={seqNumberToReply}
            setSeqNumberToReply={setSeqNumberToReply}
            scrollToBottomItem={scrollToBottomItem}
          />
        ) : (
          <PostCommentFormAuthBox
            isStudentVerificationRequired={isStudentVerificationRequired}
          />
        )}
      </div>
      <ReplyModal
        opened={isReplyModalOpened}
        close={closeReplyModal}
        topicId={topic.id}
        seqNumber={repliedSeqNumber}
        isSignedIn={isSignedIn}
      />
    </>
  );
};
